import { LitElement, html } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';

import { emit } from '@amityeko/pbc-core/dist/utilities/event';
import { getDiffBetweenDates } from '@amityeko/pbc-core/dist/utilities/datetime';

import videoIcon from '@amityeko/design-system/dist/assets/icons/video';
import stopIcon from '@amityeko/design-system/dist/assets/icons/stop';

import '@amityeko/design-system/dist/elements/dialog';
import '@amityeko/design-system/dist/elements/typography/tg-label-one';
import '@amityeko/design-system/dist/elements/toast';

import type AmityekoDialog from '@amityeko/design-system/dist/elements/dialog';
import type AmityekoToast from '@amityeko/design-system/dist/elements/toast';

import styles from './styles';

/**
 * @dependency amityeko-dialog
 * @dependency amityeko-tg-label-one
 * @dependency amityeko-toast
 *
 */

@customElement('rnr-record-video')
export default class RnRRecordVideo extends LitElement {
  static styles = styles;

  @query('#dialog') dialog!: AmityekoDialog;
  @query('#record_video') recordVideo!: HTMLVideoElement;
  @query('#toast') toast!: AmityekoToast;

  @state()
  isRecording: boolean = false;

  @state()
  stream: any;

  @state()
  mediaRecorder!: MediaRecorder;

  @state()
  tracks: MediaStreamTrack[] = [];

  @state()
  toastMessage: string = '';

  @state()
  videoURL?: string;

  @state()
  recordingTime?: string;

  @state()
  initialDate?: number;

  show() {
    this.dialog.show();
    this._onStartCamera();
  }

  private _showToast(message: string) {
    this.toastMessage = message;
    this.toast.show();
  }

  private _onClick() {
    this.dialog.show();
    this._onStartCamera();
  }
  /**
   *
   * This method will start camera but not start recording yet.
   */
  private _onSuccessCamera(stream: any) {
    this.recordVideo.style.transform = 'scaleX(-1)';
    this.recordVideo.srcObject = stream;
    this.tracks = stream.getTracks();
    this.stream = stream;
  }

  private _onStartRecording() {
    //create a new video stream
    this.mediaRecorder = new MediaRecorder(this.stream);

    //start recording video
    this.mediaRecorder.start();

    //initialize the timer
    this.initialDate = Date.now();

    this.isRecording = true;

    //create empty array for storing recorded video data
    let dataArray: any = [];

    //add recorded data to array
    this.mediaRecorder.ondataavailable = (e: any) => {
      dataArray.push(e.data);
    };

    //When recording is stopped, create blob video data with type mp4 and emit event
    this.mediaRecorder.onstop = () => {
      const videoData = new Blob(dataArray, { type: 'video/mp4' });
      dataArray = [];
      this.isRecording = false;
      this.initialDate = undefined;
      this.recordingTime = undefined;

      emit(this, 'rnr-stop-video-record', {
        detail: { video: videoData },
      });
    };
  }

  private _onTimeUpdate() {
    //update the timer in every 1s
    setInterval(() => {
      if (!this.isRecording) return;
      const finalDate = Date.now();
      this.recordingTime = getDiffBetweenDates(this.initialDate, finalDate);
    }, 1000);
  }

  private _onFailCamera(error: any) {
    if (error) this._showToast('Something went wrong while recording video');
    this.isRecording = false;
  }

  private _onStartCamera() {
    // set audio and video for video recording
    const options = {
      audio: {
        echoCancellation: true,
      },
      video: {
        width: 480,
      },
    };

    /**
     * get the user media from navigator
     * if success, call successCallback with stream
     * if failure, call failureCallback
     */
    navigator.mediaDevices
      .getUserMedia(options)
      .then(stream => {
        this._onSuccessCamera(stream);
      })
      .catch(error => this._onFailCamera(error));
  }

  private _onStopRecording() {
    //clear all the state of media recorder
    if (this.mediaRecorder?.state === 'inactive') return;
    this.mediaRecorder.stop();
    this.tracks.forEach(track => track.stop());
    this.dialog.hide();
  }

  private _onDialogBack() {
    this.dialog.hide();
    this._onStopRecording();
    emit(this, 'rnr-record-video-dialog-back');
  }

  protected render() {
    return html`
      <slot @click=${this._onClick}></slot>

      <amityeko-toast
        id="toast"
        variant="error"
        .message=${this.toastMessage}
        .duration=${5000}
      ></amityeko-toast>

      <amityeko-dialog
        id="dialog"
        label="Record Video"
        ?hasBackButton=${true}
        @amityeko-back=${this._onDialogBack}
        @amityeko-cancel=${this._onStopRecording}
      >
        <div class="content">
          <video
            id="record_video"
            class="record_video"
            @timeupdate=${this._onTimeUpdate}
            playsinline
            autoplay
            muted
          ></video>
          ${this.recordingTime && this.isRecording
            ? html`<amityeko-tg-label-one>${this.recordingTime}</amityeko-tg-label-one>`
            : null}
          <div class="control">
            ${this.isRecording
              ? html`
                  <div class="icon__wrapper">
                    <amityeko-icon color="#FB0404" @click=${this._onStopRecording}
                      >${stopIcon}</amityeko-icon
                    >
                  </div>
                `
              : html`
                  <div class="icon__wrapper">
                    <amityeko-icon
                      .iconSize=${'18px'}
                      color="#FB0404"
                      @click=${this._onStartRecording}
                      >${videoIcon}</amityeko-icon
                    >
                  </div>
                `}
          </div>
        </div>
      </amityeko-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rnr-record-video': RnRRecordVideo;
  }
}
